import { useEffect, useState } from "react";
import { ISLAND_SETTLE_MS, useIslandStore, type IslandTask } from "@/stores/islandStore";

/**
 * NotificationIsland — one morphing status object for long-running agent work.
 *
 * Replaces a stack of toasts for work that runs for minutes: a month plan, a
 * research sweep, a deck export. Toasts still own "something happened" (past
 * tense, three seconds, gone). The island owns "something is happening", and it
 * stays until the work settles.
 *
 * It is glass, which is sanctioned: case two on the allowlist in DESIGN.md
 * section 08. Transient by definition, sitting over your work without replacing
 * it. Everything under it stays solid.
 *
 * CONTENT GLASS BUDGET: at most two expressive overlay surfaces on screen. The
 * agent rail is the other one, so the island is the second and last. The quiet,
 * high-opacity navigation chrome is structural and does not enter this budget.
 */

function StatusDot({ status }: { status: IslandTask["status"] }) {
  const color =
    status === "failed"
      ? "var(--co-danger)"
      : status === "done"
        ? "var(--co-live)"
        : "var(--co-accent)";
  return (
    <span
      aria-hidden="true"
      className="relative size-[7px] flex-none rounded-full"
      style={{ background: color }}
    >
      {status === "running" ? (
        <span
          className="co-island-pulse absolute rounded-full border"
          style={{ inset: -4, borderColor: color }}
        />
      ) : null}
    </span>
  );
}

function Row({ task }: { task: IslandTask }) {
  return (
    <>
      <StatusDot status={task.status} />
      <span className="min-w-0 flex-1 truncate text-[12.5px] text-co-text2">{task.label}</span>
      {task.detail ? (
        <span className="flex-none font-mono text-[11px] text-co-faint">{task.detail}</span>
      ) : null}
    </>
  );
}

export default function NotificationIsland() {
  const tasks = useIslandStore((s) => s.tasks);
  const clear = useIslandStore((s) => s.clear);
  const [expanded, setExpanded] = useState(false);

  // A settled task reads its result, then clears itself. Effects here are
  // per-task timers rather than a single sweep, so one task finishing never
  // resets another's countdown.
  useEffect(() => {
    const settled = tasks.filter((t) => t.status !== "running");
    if (!settled.length) return;
    const timers = settled.map((t) => setTimeout(() => clear(t.id), ISLAND_SETTLE_MS));
    return () => timers.forEach(clearTimeout);
  }, [tasks, clear]);

  if (!tasks.length) return null;

  const running = tasks.filter((t) => t.status === "running");
  const lead = running[0] ?? tasks[0];
  const many = tasks.length > 1;
  const open = expanded && many;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-2 z-popover flex justify-center px-4"
      // The island reports state; it never steals focus from the work beneath.
      aria-live="polite"
    >
      <div
        className="co-glass co-island pointer-events-auto flex max-w-[min(420px,100%)] flex-col overflow-hidden border"
        style={{ borderRadius: 0 }}
        data-open={open || undefined}
      >
        <button
          type="button"
          onClick={() => many && setExpanded((v) => !v)}
          aria-expanded={many ? open : undefined}
          className="flex min-w-0 items-center gap-2.5 px-4 py-2 text-left"
          style={{ cursor: many ? "pointer" : "default" }}
        >
          {many && !open ? (
            <>
              <StatusDot status={running.length ? "running" : lead.status} />
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-co-text2">
                {running.length ? `${running.length} running` : `${tasks.length} finished`}
              </span>
              <span className="flex-none font-mono text-[11px] text-co-faint">
                {lead.label}
              </span>
            </>
          ) : (
            <Row task={lead} />
          )}
        </button>

        {open ? (
          <div className="flex flex-col border-t border-co-glass-line px-4 pb-2 pt-1.5">
            {tasks
              .filter((t) => t.id !== lead.id)
              .map((t) => (
                <span key={t.id} className="flex min-w-0 items-center gap-2.5 py-1">
                  <Row task={t} />
                </span>
              ))}
          </div>
        ) : null}

        {/* Determinate work gets a hairline under the pill. Indeterminate work
            gets nothing rather than a fake animation that implies progress. */}
        {lead.status === "running" && typeof lead.progress === "number" ? (
          <span aria-hidden="true" className="block h-px w-full bg-co-line2">
            <span
              className="block h-px w-full origin-left bg-co-accent [transition:transform_var(--spring-smooth)]"
              style={{ transform: `scaleX(${Math.min(Math.max(lead.progress, 0), 1)})` }}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
}
